/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { addDays, format, isValid, parseISO } from 'date-fns';
import {
  DEFAULT_CURRENCY,
  DEFAULT_GEO_ID,
  DEFAULT_LANGUAGE,
  DEFAULT_ONE_WAY_STAY_DAYS,
  SNAP_HOTELS_LIST_PATH,
  SNAP_LIST_PAGE_BASE_URLS,
} from '../configs/constants';
import type {
  RoomConfig,
  SearchFormState,
  SnapRecentSearchEntry,
} from '../types';
import { TripType } from '../types';
import { getNormalizedSnapListingDates } from './snapListingContext';

type SnapListingUrlConfig = {
  env?: string;
  countryCode?: string;
  currency?: string;
  language?: string;
  hotel_snap_unified_listing_enabled?: boolean;
};

type SnapListingUrlParams = SnapListingUrlConfig & {
  state: SearchFormState;
};

type SnapRecentSearchListingUrlParams = SnapListingUrlConfig & {
  entry: SnapRecentSearchEntry;
};

type SnapNativeListingConfig = SnapListingUrlConfig & {
  isLoggedIn?: boolean | number;
};

type SnapNativeListingParams = SnapNativeListingConfig & {
  state: SearchFormState;
};

type SnapRecentSearchNativeListingParams = SnapNativeListingConfig & {
  entry: SnapRecentSearchEntry;
};

type ListingDates = {
  fromDateFlight: string;
  toDateFlight: string;
  fromDateHotel: string;
  toDateHotel: string;
};

type SnapNativeTripType = 'R' | 'O';

export type SnapNativeListingProps = {
  path: 'SNAP_LIST';
  from: string;
  to: string;
  orig: string;
  dest: string;
  fromDateFlight: string;
  toDateFlight: string;
  fromDateHotel: string;
  toDateHotel: string;
  guest_adults: number[];
  guest_children: number[][];
  rooms: number;
  country_code: string;
  currency: string;
  language: string;
  isLoggedIn?: boolean | number;
  originCountryCode?: string;
  destinationCountryCode?: string;
  tripType: SnapNativeTripType;
};

const formatDate = (date?: Date): string =>
  date ? format(date, 'yyyy-MM-dd') : '';

const formatAddedDate = (dateValue?: string, days = 0): string => {
  if (!dateValue) {
    return '';
  }

  const parsedDate = parseISO(dateValue);
  return isValid(parsedDate)
    ? format(addDays(parsedDate, days), 'yyyy-MM-dd')
    : '';
};

const getBaseUrl = (
  env?: string,
  hotel_snap_unified_listing_enabled?: boolean,
): string => {
  const listingUrl =
    env === 'prd'
      ? SNAP_LIST_PAGE_BASE_URLS.prod
      : SNAP_LIST_PAGE_BASE_URLS.stg;

  return hotel_snap_unified_listing_enabled
    ? listingUrl
    : listingUrl.replace(SNAP_HOTELS_LIST_PATH, '/snap/hotels/list');
};

/**
 * Legacy SNAP listing deeplink encodes guests per room, not as a total:
 * `adults=1,4` means room 1 has 1 adult and room 2 has 4. Children and infants
 * use `<1-basedRoomIndex>_<age>` entries. The listing page's edit-search widget
 * parses these back into per-room counts, so the room index must be preserved.
 */
const getAdultsString = (rooms: RoomConfig[]): string =>
  rooms.map((room) => room.adults).join(',');

const getGuestAdults = (rooms: RoomConfig[]): number[] =>
  rooms.map((room) => room.adults);

const getGuestChildren = (rooms: RoomConfig[]): number[][] =>
  rooms.map((room) => [
    ...(room.children || []).map((child) => child.age),
    ...Array.from({ length: room.infants || 0 }, () => 1),
  ]);

const getChildAgeString = (rooms: RoomConfig[]): string =>
  rooms
    .flatMap((room, index) =>
      (room.children || []).map((child) => `${index + 1}_${child.age}`),
    )
    .join(',');

const getInfantAgeString = (rooms: RoomConfig[]): string =>
  rooms
    .flatMap((room, index) =>
      Array.from({ length: room.infants || 0 }, () => `${index + 1}_1`),
    )
    .join(',');

const getNativeTripType = (tripType: TripType | string): SnapNativeTripType => {
  const normalizedTripType = String(tripType).toUpperCase();

  return normalizedTripType === TripType.ONE_WAY ||
    normalizedTripType === 'ONE_WAY' ||
    normalizedTripType === 'ONE-WAY' ||
    normalizedTripType === 'O'
    ? 'O'
    : 'R';
};

const getStateDates = (state: SearchFormState): ListingDates => {
  const isOneWay = getNativeTripType(state.tripType) === 'O';
  const flightReturnDate = isOneWay ? undefined : state.selectedDates.return;
  const hotelCheckIn = state.selectDifferentDates
    ? state.hotelDates.checkIn
    : state.selectedDates.depart;
  const hotelCheckOut = state.selectDifferentDates
    ? state.hotelDates.checkOut
    : isOneWay && state.selectedDates.depart
      ? addDays(state.selectedDates.depart, DEFAULT_ONE_WAY_STAY_DAYS)
      : flightReturnDate;

  return getNormalizedSnapListingDates({
    fromDateFlight: formatDate(state.selectedDates.depart),
    toDateFlight: formatDate(flightReturnDate),
    fromDateHotel: formatDate(hotelCheckIn),
    toDateHotel: formatDate(hotelCheckOut),
  });
};

const getRecentSearchDates = (entry: SnapRecentSearchEntry): ListingDates => {
  const isOneWay = getNativeTripType(entry.tripType) === 'O';
  const toDateFlight = isOneWay ? '' : entry.returnDate || '';
  const fromDateHotel = entry.selectDifferentDates
    ? entry.hotelCheckIn || entry.departDate || ''
    : entry.departDate || '';
  const toDateHotel =
    entry.selectDifferentDates && entry.hotelCheckOut
      ? entry.hotelCheckOut
      : isOneWay
        ? formatAddedDate(entry.departDate, DEFAULT_ONE_WAY_STAY_DAYS)
        : entry.returnDate || '';

  return getNormalizedSnapListingDates({
    fromDateFlight: entry.departDate || '',
    toDateFlight,
    fromDateHotel,
    toDateHotel,
  });
};

const getListingTripType = (
  tripType: TripType | string,
  dates: ListingDates,
): SnapNativeTripType =>
  dates.toDateFlight ? 'R' : getNativeTripType(tripType);

const buildUrl = ({
  adults,
  children,
  countryCode,
  currency,
  destination,
  env,
  fromDateFlight,
  fromDateHotel,
  infants,
  language,
  hotel_snap_unified_listing_enabled,
  origin,
  rooms,
  toDateFlight,
  toDateHotel,
  tripType,
}: SnapListingUrlConfig & {
  adults: string;
  children: string;
  destination: string;
  fromDateFlight: string;
  fromDateHotel: string;
  infants: string;
  origin: string;
  rooms: number;
  toDateFlight: string;
  toDateHotel: string;
  tripType: SnapNativeTripType;
}): string => {
  const params = new URLSearchParams({
    adults,
    country_code: countryCode || DEFAULT_GEO_ID,
    currency: currency || DEFAULT_CURRENCY,
    dest: destination,
    fromDateFlight,
    fromDateHotel,
    language: language || DEFAULT_LANGUAGE,
    orig: origin,
    rooms: rooms.toString(),
    toDateFlight,
    toDateHotel,
    tripType,
    ...(children && { children }),
    ...(infants && { infants }),
  });

  return `${getBaseUrl(env, hotel_snap_unified_listing_enabled)}?${params.toString()}`;
};

export const buildSnapListingUrl = ({
  state,
  env,
  countryCode,
  currency,
  language,
  hotel_snap_unified_listing_enabled,
}: SnapListingUrlParams): string => {
  const dates = getStateDates(state);
  const tripType = getListingTripType(state.tripType, dates);

  return buildUrl({
    adults: getAdultsString(state.rooms),
    children: getChildAgeString(state.rooms),
    countryCode,
    currency,
    destination: state.destinationStation?.StationCode || '',
    env,
    fromDateFlight: dates.fromDateFlight,
    fromDateHotel: dates.fromDateHotel,
    hotel_snap_unified_listing_enabled,
    infants: getInfantAgeString(state.rooms),
    language,
    origin: state.originStation?.StationCode || '',
    rooms: state.rooms.length,
    toDateFlight: dates.toDateFlight,
    toDateHotel: dates.toDateHotel,
    tripType,
  });
};

export const buildSnapRecentSearchListingUrl = ({
  entry,
  env,
  countryCode,
  currency,
  language,
  hotel_snap_unified_listing_enabled,
}: SnapRecentSearchListingUrlParams): string => {
  const dates = getRecentSearchDates(entry);
  const tripType = getListingTripType(entry.tripType, dates);

  return buildUrl({
    adults: getAdultsString(entry.rooms),
    children: getChildAgeString(entry.rooms),
    countryCode,
    currency,
    destination: entry.to.StationCode || '',
    env,
    fromDateFlight: dates.fromDateFlight,
    fromDateHotel: dates.fromDateHotel,
    hotel_snap_unified_listing_enabled,
    infants: getInfantAgeString(entry.rooms),
    language,
    origin: entry.from.StationCode || '',
    rooms: entry.rooms.length,
    toDateFlight: dates.toDateFlight,
    toDateHotel: dates.toDateHotel,
    tripType,
  });
};

export const buildSnapNativeListingProps = ({
  state,
  countryCode,
  currency,
  language,
  isLoggedIn,
}: SnapNativeListingParams): SnapNativeListingProps => {
  const dates = getStateDates(state);
  const tripType = getListingTripType(state.tripType, dates);
  const origin = state.originStation?.StationCode || '';
  const destination = state.destinationStation?.StationCode || '';

  return {
    path: 'SNAP_LIST',
    from: origin,
    to: destination,
    orig: origin,
    dest: destination,
    ...dates,
    guest_adults: getGuestAdults(state.rooms),
    guest_children: getGuestChildren(state.rooms),
    rooms: state.rooms.length,
    country_code: countryCode || DEFAULT_GEO_ID,
    currency: currency || DEFAULT_CURRENCY,
    language: language || DEFAULT_LANGUAGE,
    isLoggedIn: isLoggedIn ?? false,
    originCountryCode: state.originStation?.CountryCode,
    destinationCountryCode: state.destinationStation?.CountryCode,
    tripType,
  };
};

export const buildSnapRecentSearchNativeListingProps = ({
  entry,
  countryCode,
  currency,
  language,
  isLoggedIn,
}: SnapRecentSearchNativeListingParams): SnapNativeListingProps => {
  const dates = getRecentSearchDates(entry);
  const tripType = getListingTripType(entry.tripType, dates);
  const origin = entry.from.StationCode || '';
  const destination = entry.to.StationCode || '';

  return {
    path: 'SNAP_LIST',
    from: origin,
    to: destination,
    orig: origin,
    dest: destination,
    ...dates,
    guest_adults: getGuestAdults(entry.rooms),
    guest_children: getGuestChildren(entry.rooms),
    rooms: entry.rooms.length,
    country_code: countryCode || DEFAULT_GEO_ID,
    currency: currency || DEFAULT_CURRENCY,
    language: language || DEFAULT_LANGUAGE,
    isLoggedIn: isLoggedIn ?? false,
    originCountryCode: entry.from.CountryCode,
    destinationCountryCode: entry.to.CountryCode,
    tripType,
  };
};
